import {AxiosError, AxiosResponse, type RawAxiosRequestConfig} from "axios";
import {LogoutForm} from "components/src/auth/LogoutForm";
import {SavedLocation} from "components/src/auth/SavedLocation";
import {FloatingLabel} from "components/src/common/FloatingLabel";
import {RenderAlert} from "components/src/common/RenderAlert";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {logger} from "components/src/utils/Logger";
import {StringUtils} from "components/src/utils/StringUtils";
import {Utils} from "components/src/utils/Utils";
import {useRef, useState} from "react";
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from "react-bootstrap/Modal";
import {useNavigate} from "react-router-dom";

interface TfaProps {
    validateOTPFunc : (totp : string, options?: RawAxiosRequestConfig) => Promise<AxiosResponse<void, string>>
}

export const Tfa = ({validateOTPFunc} : TfaProps) =>
{
    /**
     * Navigate is used to navigate to the main page after signup
     */
    const navigate = useNavigate();
    /**
     * The error message if some error occurred which is not a validation error
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The OTP code
     */
    const [totp, setTotp] = useState<string>("");
    // store a reference to the invisible logout submit button which can be used to programmatically submit
    // the logout form when the user selects logout from the menu
    const logoutSubmitButtonRef = useRef<HTMLButtonElement>(null);
    // store a reference to the TOTP input field which can be used to programmatically focus the input field
    const totpInputRef = useRef<HTMLInputElement>(null);
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    const validateOTP = () =>
    {
        if (totp === undefined) {
            throw new Error("totp is undefined");
        }

        validateOTPFunc(totp, {
            headers: {
                "TOTP" : totp
            }
        })
        .then(() =>
        {
            navigate(SavedLocation.getSavedLocation()??"/");
        })
        .catch((err: Error | AxiosError) =>
        {
            logger.error("Error validating TOTP", err);

            if (err instanceof AxiosError && err.response?.status === 401)
            {
                // the code was correct, but it was already used to sign in
                if (err.response?.headers["www-authenticate"] === "TOTP-USED") {
                    setErrorMessage(t("tfa.code-already-used"))
                }
                // the code was correct, but it expired, or the clock of the device is off
                else if (err.response?.headers["www-authenticate"] === "TOTP-EXPIRED") {
                    setErrorMessage(t("tfa.code-expired"))
                }
                else {
                    setErrorMessage(t("tfa.invalid-2fa-code"))
                }
            }
            else {
                setErrorMessage(Utils.getErrorMessage(err, t));
            }
        })
    }

    return (
        <Modal show={true}
               centered={true}
               onHide={() => logoutSubmitButtonRef.current?.click()}
               onEntered={() => totpInputRef.current?.focus()} backdrop={"static"}
        >
            <Modal.Header closeButton={true}>
                <Modal.Title>{t("tfa.title")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <RenderAlert message={errorMessage}/>

                <div>{t("tfa.text")}</div>

                <FloatingLabel label={t('tfa.otp-label')} className="text-secondary mt-3">
                    <Form.Control type="text"
                                  name="totp"
                                  placeholder={t('tfa.otp-label')}
                                  ref={totpInputRef}
                                  onChange={e => setTotp(e.target.value)}/>
                </FloatingLabel>

                <LogoutForm ref={logoutSubmitButtonRef}/>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="primary" type="submit" onClick={validateOTP} disabled={StringUtils.isEmpty(totp)}>
                    {t('tfa.login-button')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
